/********************************************************************************
 * COMPONENT: CHECKOTUT SUCCESS
 * ------------------------------------------------------------------------------
 * This component is responsible for rendering the success message after a
 * successful checkout. Really just pulling the basic data from stripe
 * landing page results.
 * *****************************************************************************/

import React, { useEffect, useState } from 'react';
import { Stack, Box, Typography, Divider, Skeleton, Grid } from '@mui/material';
import { API } from 'aws-amplify';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api';
import { getCheckout } from 'src/graphql/queries';
import { GetCheckoutQuery, GetCheckoutQueryVariables } from 'src/graphql/elomapi';
import { renderPrice } from 'src/utils';
import { capitalize } from 'lodash';

// POSTHOG TRACKING
import posthog from 'posthog-js';

const CheckoutSuccess = () => {
  const [checkoutData, setCheckoutData] = useState<GetCheckoutQuery | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    setLoading(true);

    const getCheckoutData = async () => {
      const sessionId = location.search.replace('?session_id=', '');

      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const variables = {
          sessionId,
        } as GetCheckoutQueryVariables;

        const response = (await API.graphql({
          query: getCheckout,
          variables,
          authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
        })) as { data: GetCheckoutQuery };

        const checkoutData = response.data;
        setCheckoutData(checkoutData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching checkout data', error);
        setError(true);
        setLoading(false);
      }
    };

    getCheckoutData();
  }, []);


  useEffect(() => {
    
    const anonymouseId = posthog.get_distinct_id();
    
    if (!checkoutData) {
      posthog.capture('checkout receipt loading', {
        anonymous_id: anonymouseId,
        product_id: 'unknown',
        product_slug: 'unknown',
      });
    } else {
      posthog.capture('checkout receipt viewed', {
        anonymous_id: anonymouseId,
        product_id: 'unknown',
        product_slug: 'unknown',
      });
    }    
  }, [checkoutData]);

  // console.log(checkoutData);

  // * DISCOUNT: If discount, render the price or return null
  const discount = checkoutData?.getCheckout?.totalPrice?.discount
    ? renderPrice(checkoutData?.getCheckout?.totalPrice?.discount)
    : null;

  // * TAX: If tax, render the price or return null
  const tax = checkoutData?.getCheckout?.totalPrice?.tax
    ? renderPrice(checkoutData?.getCheckout?.totalPrice?.tax ?? 0)
    : null;

  // * SHIPPING: If shipping, render the price or return 'FREE'
  const shipping = checkoutData?.getCheckout?.totalPrice?.shipping
    ? renderPrice(checkoutData?.getCheckout?.totalPrice?.shipping)
    : 'FREE';

  // * TOTAL PRICE: Render the total price
  const totalPrice = renderPrice(checkoutData?.getCheckout?.totalPrice?.total ?? 0);

  if (loading) {
    return (
      <Stack spacing={1} width={'100%'}>
        <Skeleton variant="rectangular" height={30} width={'100%'} />
        <Divider flexItem />
        <Skeleton variant="rectangular" height={30} width={'100%'} />
        <Divider flexItem />
        <Skeleton variant="rectangular" height={30} width={'100%'} />
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Skeleton variant="rectangular" height={'240px'} width={'100%'} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Skeleton variant="rectangular" height={'240px'} width={'100%'} />
          </Grid>
        </Grid>
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={2} width={'100%'} pt={'3rem'}>
        {/* SHIPPING */}
        <Divider flexItem />
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography variant="body2">Shipping</Typography>
          <Typography variant="body2">{shipping}</Typography>
        </Box>
        <Divider flexItem />

        {/* DISCOUNT */}
        {discount && (
          <>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography variant="body2">Discount</Typography>
              <Typography variant="body2">{discount}</Typography>
            </Box>
            <Divider flexItem />
          </>
        )}
        {/* TAX */}
        {tax && (
          <>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography variant="body2">Tax</Typography>
              <Typography variant="body2">{tax}</Typography>
            </Box>
            <Divider flexItem />
          </>
        )}

        {/* Total Price */}
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography variant="body2">Total</Typography>
          <Typography variant="body2">{totalPrice}</Typography>
        </Box>
        <Divider flexItem />
        <Box
          display="flex"
          flexWrap="wrap"
          pt="4rem"          
        >
          <Grid container spacing={2}>
            {/* * SHIPPING ADDRESS */}
            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                
                <Typography variant="body1">Shipping</Typography>
                <Box>
                  <Typography variant="body2">{checkoutData?.getCheckout?.name}</Typography>
                  <Typography variant="body2">
                    {checkoutData?.getCheckout?.shippingAddress?.street}
                  </Typography>
                  <Typography variant="body2">
                    {checkoutData?.getCheckout?.shippingAddress?.street2}
                  </Typography>
                  <Typography variant="body2">
                    {checkoutData?.getCheckout?.shippingAddress?.city + ' '}
                    {checkoutData?.getCheckout?.shippingAddress?.state + ', '}
                    {checkoutData?.getCheckout?.shippingAddress?.zip}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* * PAYMENT INFO */}
            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                
                <Typography variant="body1">Payment</Typography>
                <Typography variant="body2">
                  {capitalize(checkoutData?.getCheckout?.paymentMethodType ?? '') + ' ending in '}
                  {checkoutData?.getCheckout?.paymentMethodDescription}
                </Typography>
                <Divider />
                <Typography variant="body1">Support</Typography>
                <Typography variant="body2" component={'a'} href='https://help.elo.health/support/home'>
                  Elo Help Center
                  </Typography>
                <Typography variant="body2" component={'a'} href='mailto:elo@elo.health'>elo@elo.health</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </>
  );
};

export default CheckoutSuccess;
